import { useState } from 'react';

export default function ServicesSection() {
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | ''>('');

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Validar longitud del mensaje
      if (formData.message.length > 500) {
        setSubmitStatus('error');
        return;
      }

      // Preparar datos del formulario usando formato application/x-www-form-urlencoded
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('message', formData.message);

      const response = await fetch(
        'https://readdy.ai/api/form/d3dl5u6ug1ep97qtdoag',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formDataToSend.toString(),
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
        });
        setTimeout(() => {
          setShowConsultationForm(false);
          setSubmitStatus('');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      // Limpiar mensaje de estado después de 5 segundos (en caso de error)
      setTimeout(() => setSubmitStatus(''), 5000);
    }
  };

  return (
    <section
      id="services-section"
      className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 relative bg-black"
    >
      {/* Fondo estrellado */}
      <div className="absolute inset-0">
        {[...Array(40)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`,
            }}
          >
            <div className="w-0.5 h-0.5 rounded-full bg-gold-500"></div>
          </div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Título de la sección */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 lg:mb-8 leading-tight font-serif text-gold-500">
            Nuestros Servicios
          </h2>
          <div className="w-16 sm:w-24 lg:w-32 h-px mx-auto mb-6 sm:mb-8 lg:mb-10 bg-gradient-to-r from-transparent via-gold-500 to-transparent"></div>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed px-2 font-sans">
            Elige el paquete perfecto para tu familia y crea recuerdos inolvidables esta Navidad. Tambien Vea los espacios disponibles para mejor comodidad. 
          </p>
        </div>

        {/* Grid de servicios */}
        <div className="grid lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
          {/* Classic Christmas */}
          <div className="relative bg-gradient-to-b from-gray-900/60 to-gray-800/40 backdrop-blur-sm border border-gold-500/30 rounded-2xl lg:rounded-3xl p-6 sm:p-8 lg:p-10 hover:scale-105 transition-all duration-300">
            <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-6 lg:mb-8 flex items-center justify-center rounded-2xl lg:rounded-3xl bg-gradient-to-br from-gold-500 to-gold-600">
              <i className="ri-gift-fill text-2xl lg:text-3xl text-black"></i>
            </div>
            <div className="text-center mb-6 lg:mb-8">
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3 lg:mb-4 font-serif text-gold-500">
                Classic Christmas
              </h3>
              <p className="text-sm sm:text-base lg:text-lg text-gray-300 leading-relaxed font-sans">
                Ideal para familias que buscan algo rápido y accesible con la
                magia navideña.
              </p>
            </div>
            <ul className="space-y-3 lg:space-y-4 mb-8 lg:mb-10">
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                25 minutos de sesión
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                10 fotos digitales editadas
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                1 set navideño a elección
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                Grupo familiar hasta 5 participantes
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                ¡Pet friendly!
              </li>
            </ul>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 lg:mb-6 font-serif text-gold-500">
                $189
              </div>
              <a
                href="/season-ended"
                className="w-full py-3 lg:py-4 px-6 lg:px-8 rounded-xl lg:rounded-2xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer text-gold-500 border border-gold-500 bg-transparent hover:bg-gold-500 hover:text-black font-sans inline-block text-center text-sm sm:text-base lg:text-lg"
              >
                Quiero mi Sesión
              </a>
              <div className="mt-3 lg:mt-4 text-xs sm:text-sm text-gray-400 font-sans">
                💳 <span className="text-gold-400">Compra ahora, paga después</span>
                <br />
                Financiamiento 0% con Klarna y Affirm
              </div>
            </div>
          </div>

          {/* Magic Christmas Package */}
          <div className="relative bg-gradient-to-b from-gray-900/60 to-gray-800/40 backdrop-blur-sm border-2 border-gold-500 rounded-2xl lg:rounded-3xl p-6 sm:p-8 lg:p-10 hover:scale-105 transition-all duration-300">
            <div className="absolute -top-3 lg:-top-4 left-1/2 transform -translate-x-1/2">
              <span className="px-4 lg:px-6 py-1 lg:py-2 text-black text-xs sm:text-sm lg:text-base font-semibold rounded-full whitespace-nowrap bg-gold-500 font-sans">
                MÁS POPULAR
              </span>
            </div>
            <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-6 lg:mb-8 flex items-center justify-center rounded-2xl lg:rounded-3xl bg-gradient-to-br from-gold-500 to-gold-600">
              <i className="ri-star-fill text-2xl lg:text-3xl text-black"></i>
            </div>
            <div className="text-center mb-6 lg:mb-8">
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3 lg:mb-4 font-serif text-gold-500">
                Magic Christmas
              </h3>
              <p className="text-sm sm:text-base lg:text-lg text-gray-300 leading-relaxed font-sans">
                El que más van a elegir: buen balance entre precio y valor.
              </p>
            </div>
            <ul className="space-y-3 lg:space-y-4 mb-8 lg:mb-10">
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                45 minutos de sesión
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                Experiencia en los 2 sets navideños
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                Todas las fotos digitales
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                Galería online privada por 60 días
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                Autorización de impresión (Print Release)
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                Grupo familiar hasta 5 participantes
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                ¡Pet friendly!
              </li>
            </ul>
            <div className="text-center">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 lg:mb-6 font-serif text-gold-500">
                $289
              </div>
              <a
                href="/season-ended"
                className="w-full py-3 lg:py-4 px-6 lg:px-8 rounded-xl lg:rounded-2xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer text-black bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 font-sans inline-block text-center text-sm sm:text-base lg:text-lg"
              >
                Quiero mi Sesión
              </a>
              <div className="mt-3 lg:mt-4 text-xs sm:text-sm text-gray-400 font-sans">
                💳 <span className="text-gold-400">Compra ahora, paga después</span>
                <br />
                Financiamiento 0% con Klarna y Affirm
              </div>
            </div>
          </div>

          {/* Starlight VIP Package */}
          <div className="relative bg-gradient-to-b from-gray-900/60 to-gray-800/40 backdrop-blur-sm border border-gold-500/30 rounded-2xl lg:rounded-3xl p-6 sm:p-8 lg:p-10 hover:scale-105 transition-all duration-300">
            <div className="w-16 h-16 lg:w-20 lg:h-20 mx-auto mb-6 lg:mb-8 flex items-center justify-center rounded-2xl lg:rounded-3xl bg-gradient-to-br from-gold-500 to-gold-600">
              <i className="ri-vip-crown-fill text-2xl lg:text-3xl text-black"></i>
            </div>

            <div className="text-center mb-6 lg:mb-8">
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3 lg:mb-4 font-serif text-gold-500">
                Starlight VIP
              </h3>
              <p className="text-sm sm:text-base lg:text-lg text-gray-300 leading-relaxed font-sans">
                Para quienes buscan exclusividad y una experiencia más completa.
              </p>
            </div>

            <ul className="space-y-3 lg:space-y-4 mb-8 lg:mb-10">
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                90 minutos de sesión
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                Acceso completo a los 2 sets + cambios de vestuario
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                Todas las fotos digitales en alta resolución
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                Galería privada extendida por 90 días
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                $75 de crédito para impresiones o productos online
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                Autorización de impresión (Print Release)
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                Grupo familiar hasta 6 participantes
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                ¡Pet friendly!
              </li>
              <li className="flex items-center text-sm sm:text-base lg:text-lg text-gray-300 font-sans">
                <i className="ri-check-line text-lg lg:text-xl mr-3 flex-shrink-0 text-gold-500"></i>
                Bebida espumosa para celebrar la ocasión
              </li>
            </ul>

            <div className="text-center">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 lg:mb-6 font-serif text-gold-500">
                $389
              </div>
              <a
                href="/season-ended"
                className="w-full py-3 lg:py-4 px-6 lg:px-8 rounded-xl lg:rounded-2xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer text-gold-500 border border-gold-500 bg-transparent hover:bg-gold-500 hover:text-black font-sans inline-block text-center text-sm sm:text-base lg:text-lg"
              >
                Quiero mi Sesión
              </a>
              <div className="mt-3 lg:mt-4 text-xs sm:text-sm text-gray-400 font-sans">
                💳 <span className="text-gold-400">Compra ahora, paga después</span>
                <br />
                Financiamiento 0% con Klarna y Affirm
              </div>
            </div>
          </div>
        </div>

        {/* CTA adicional */}
        <div className="text-center mt-12 sm:mt-16 lg:mt-20">
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-6 lg:mb-8 font-sans">
            ¿Necesitas ayuda para reservar una sesión?
          </p>
          <button
            onClick={() => setShowConsultationForm(true)}
            className="text-black font-semibold py-3 sm:py-4 lg:py-5 px-6 sm:px-8 lg:px-10 rounded-xl lg:rounded-2xl transition-all duration-300 whitespace-nowrap cursor-pointer bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 font-sans text-base sm:text-lg lg:text-xl"
          >
            Consulta Personalizada
          </button>
        </div>
      </div>

      {/* Modal de consulta personalizada */}
      {showConsultationForm && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 99999 }}
          onClick={() => setShowConsultationForm(false)}
        >
          <div
            className="bg-gradient-to-b from-gray-900/90 to-gray-800/90 backdrop-blur-sm border border-gold-500/30 rounded-2xl lg:rounded-3xl p-6 sm:p-8 lg:p-10 max-w-md w-full max-h-[90vh] overflow-y-auto"
            style={{ zIndex: 100000 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6 lg:mb-8">
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold font-serif text-gold-500">
                Consulta Personalizada
              </h3>
              <button
                onClick={() => setShowConsultationForm(false)}
                className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-full bg-gray-700/50 hover:bg-gray-600/50 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-gray-300 text-lg lg:text-xl"></i>
              </button>
            </div>

            <p className="text-gray-300 text-sm sm:text-base lg:text-lg mb-6 lg:mb-8 font-sans">
              Cuéntanos sobre tus necesidades y te ayudaremos a elegir el paquete
              perfecto para tu sesión navideña.
            </p>

            <form
              id="consultation-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-4 lg:space-y-6"
            >
              <div>
                <label className="block font-medium mb-2 text-sm sm:text-base lg:text-lg text-gold-500 font-sans">
                  Nombre Completo
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-gray-800/50 border border-gray-600/50 rounded-xl lg:rounded-2xl px-4 lg:px-6 py-3 lg:py-4 text-white focus:outline-none focus:border-gold-500 transition-colors text-sm sm:text-base lg:text-lg font-sans"
                  placeholder="Tu nombre completo"
                />
              </div>

              <div>
                <label className="block font-medium mb-2 text-sm sm:text-base lg:text-lg text-gold-500 font-sans">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full bg-gray-800/50 border border-gray-600/50 rounded-xl lg:rounded-2xl px-4 lg:px-6 py-3 lg:py-4 text-white focus:outline-none focus:border-gold-500 transition-colors text-sm sm:text-base lg:text-lg font-sans"
                  placeholder="tu@email.com"
                />
              </div>

              <div>
                <label className="block font-medium mb-2 text-sm sm:text-base lg:text-lg text-gold-500 font-sans">
                  Teléfono
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800/50 border border-gray-600/50 rounded-xl lg:rounded-2xl px-4 lg:px-6 py-3 lg:py-4 text-white focus:outline-none focus:border-gold-500 transition-colors text-sm sm:text-base lg:text-lg font-sans"
                  placeholder="Tu número de teléfono"
                />
              </div>

              <div>
                <label className="block font-medium mb-2 text-sm sm:text-base lg:text-lg text-gold-500 font-sans">
                  Mensaje
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full bg-gray-800/50 border border-gray-600/50 rounded-xl lg:rounded-2xl px-4 lg:px-6 py-3 lg:py-4 text-white focus:outline-none focus:border-gold-500 transition-colors text-sm sm:text-base lg:text-lg font-sans"
                  placeholder="Cuéntanos más sobre tu proyecto..."
                />
              </div>

              {submitStatus === 'error' && (
                <p className="text-red-500 text-sm">
                  Ocurrió un error al enviar el formulario. Por favor, verifica los
                  datos y vuelve a intentarlo.
                </p>
              )}
              {submitStatus === 'success' && (
                <p className="text-green-500 text-sm">
                  ¡Mensaje enviado con éxito! Nos pondremos en contacto pronto.
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 lg:py-4 px-6 lg:px-8 rounded-xl lg:rounded-2xl font-semibold transition-all duration-300 ${
                  isSubmitting
                    ? 'bg-gold-400 cursor-not-allowed'
                    : 'bg-gold-500 hover:bg-gold-600'
                } text-black font-sans text-sm sm:text-base lg:text-lg`}
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Consulta'}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
